"use strict";

class ValidationRule {
    /**
     * 
     * @param {string} attribute 
     * @param {string|null} type 
     * @param {Object} ruleOpt 
     * @returns {function}
     */
    toFunction(attribute, type, ruleOpt) {
        const handler = this._handle(attribute, type);

        return (value, allValues) => {
            let skipNext = this._shouldSkipNextRules(value, allValues, attribute, type, ruleOpt);

            if (skipNext !== null) {
                ruleOpt.skipNextRules = skipNext;
            }

            if (ruleOpt.skipNextRules) {
                return true;
            }
            return handler(value, allValues);
        };
    }

    /**
     * 
     * @param {*} value 
     * @param {Object} allValues 
     * @param {String} attribute 
     * @param {String|null} type 
     * @param {Object} ruleOpt 
     * @returns {boolean|null}
     */
    _shouldSkipNextRules(value, allValues, attribute, type, ruleOpt) {
        return null;
    }

    validate(attribute, type, value, obj) {
        return this.toFunction(attribute, type)(value, obj);
    }

    /**
     * 
     * @param {String} attribute 
     * @param {String|null} type 
     * @param {Object} ruleOpt 
     * @returns {Function}
     */
    _handle(attribute, type) {
        return (v) => true;
    }
}

class ClosureRule extends ValidationRule {
    constructor(handler) {
        super();
        this.handler = handler;
    }

    _handle(attribute, type) {
        return handler;
    }
}

class AcceptedRule extends ValidationRule {
    _handle(attribute, type) {
        return (v) =>
            !!v || trans("validation.accepted", { attribute });
    }
}

class ArrayRule extends ValidationRule {

    constructor() {
        super();
        this.type = 'array';
    }

    _handle(attribute, type) {
        return (v) =>
            !!v || trans("validation.accepted", { attribute });
    }
}
class StringRule extends ValidationRule {

    constructor() {
        super();
        this.type = 'string';
    }

    _handle(attribute, type) {
        return (v) => true;
    }
}

class NullableRule extends ValidationRule {
    _shouldSkipNextRules(value, allValues, attribute, type, ruleOpt) {
        return (
            value === 0
            || value === '0'
            || value === ''
            || (value + '').trim() === ''
            || value === null
            || value === undefined
            || value === NaN
        );
    }

    _handle(attribute, type) {
        return (v) => true;
    }
}
class RequiredRule extends ValidationRule {
    _handle(attribute, type) {
        return (v) =>
            !!(v !== undefined && v !== null && v !== "") ||
            trans("validation.required", { attribute });
    }
}
class NumericRule extends ValidationRule {
    constructor() {
        super();
        this.type = 'numeric';
    }
    _handle(attribute, type) {
        return (v) => (!isNaN(parseFloat(v))) || trans("validation.numeric", { attribute });
    }
}
class RequiredWithRule extends ValidationRule {
    constructor(otherFields) {
        super();
        if (typeof otherFields === "string") {
            otherFields = otherFields.split("|");
        }
        this.otherFields = otherFields;
    }
    _handle(attribute, type) {
        return (v, obj) => {
            if (obj && obj[otherField]) {
                return Rule.required().validate(attribute, type, v, obj);
            } else {
                return true;
            }
        };
    }
}
class PresentRule extends ValidationRule {
    _handle(attribute, type) {
        return (v) =>
            !!(v && v.length > 0) ||
            trans("validation.present", { attribute });
    }
}

class MinRule extends ValidationRule {
    constructor(min) {
        super();
        this.min = min;
    }
    _handle(attribute, type) {
        return (v) => {
            const min = this.min;
            if (!['numeric', 'string', 'array'].includes(type)) {
                if (Array.isArray(v))
                    type = 'array';
                if (v !== '' && !isNaN(v) && !isNaN(parseFloat(v)))
                    type = 'numeric';
            }

            switch (type) {
                default:
                case 'string':
                    return !!(v && (v + "").length >= min) || trans("validation.min.string", { attribute, min });
                case 'numeric':
                    return !!(v && parseFloat(v) !== NaN && parseFloat(v) >= min) || trans("validation.min.numeric", { attribute, min });;
                case 'array':
                    return !!(v && Array.isArray(v) && v.length >= min) || trans("validation.min.array", { attribute, min })
            }
        };
    }
}
class MaxRule extends ValidationRule {
    constructor(max) {
        super();
        this.max = max;
    }
    _handle(attribute, type) {
        return (v) => {
            if (!['numeric', 'string', 'array'].includes(type)) {
                if (Array.isArray(v))
                    type = 'array';
                if (v !== '' && !isNaN(v) && !isNaN(parseFloat(v)))
                    type = 'numeric';
            }

            switch (type) {
                default:
                case 'string':
                    return !!(v && (v + "").length <= this.max) || trans("validation.max.string", { attribute, max: this.max });
                case 'numeric':
                    return !!(v && v <= this.max) || trans("validation.max.numeric", { attribute, max: this.max });;
                case 'array':
                    return !!(v && Array.isArray(v) && v.length <= this.max) || trans("validation.max.array", { attribute, max: this.max })
            }
        };
    }
}

class RequiredIfRule extends ValidationRule {
    constructor(otherField, values) {
        super();
        this.otherField = otherField;
        this.values = values;
    }

    _handle(attribute, type) {
        return (v, obj) => {
            const otherValue = obj && obj[this.otherField];
            let result = false;

            if (typeof this.values === 'function') {
                result = this.values(otherValue, obj);
            } else if (typeof this.values === 'array') {
                result = this.values.includes(otherValue);
            } else {
                result = otherValue === this.values;
            }

            return result || trans("validation.required_if", { attribute, other: this.otherField, value: otherValue });
        }
    }
}

class DateRule extends ValidationRule {
    constructor() {
        super();
        this.type = 'date';
    }

    _handle(attribute, type) {
        return (v, obj) => {
            return (!!Date.parse(v)) || trans("validation.date", { attribute });
        }
    }
}


export {
    ValidationRule,
    ClosureRule,

    ArrayRule,
    StringRule,

    AcceptedRule,
    RequiredRule,
    NullableRule,//TODO
    RequiredIfRule,
    RequiredWithRule,
    NumericRule,
    PresentRule,
    MinRule,
    MaxRule,
    DateRule,
}